import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { Nullable } from 'cng-common/models/utils'

import { FlagToReassign, RoleToReassign } from '../models'

export interface ReassignmentContextType {
  availableContacts: IContact[]
  rolesToReassign: RoleToReassign[]
  flagsToReassign: FlagToReassign[]
  updateRoleAssignment: (role: string, contact: Nullable<IContact>) => void
  updateFlagAssignment: (flagCode: string, contact: Nullable<IContact>) => void
  setAvailableContacts: (contacts: IContact[]) => void
  setRolesToReassign: (roles: RoleToReassign[]) => void
  setFlagsToReassign: (flags: FlagToReassign[]) => void
}

export const ReassignmentContext = createContext<ReassignmentContextType | null>(null)

export const useReassignment = (): ReassignmentContextType => {
  const context = useContext(ReassignmentContext)
  if (!context) {
    throw new Error('useReassignment must be used within ReassignmentProvider')
  }
  return context
}

export interface ReassignmentProviderProps {
  children: React.ReactNode
}

export const ReassignmentProvider: React.FC<ReassignmentProviderProps> = ({ children }) => {
  const [availableContacts, setAvailableContacts] = useState<IContact[]>([])
  const [rolesToReassign, setRolesToReassign] = useState<RoleToReassign[]>([])
  const [flagsToReassign, setFlagsToReassign] = useState<FlagToReassign[]>([])

  const updateRoleAssignment = useCallback((role: string, contact: Nullable<IContact>) => {
    setRolesToReassign((prevRoles) =>
      prevRoles.map((r) => (r.role === role ? { ...r, contact } : r))
    )
  }, [])

  const updateFlagAssignment = useCallback((flagCode: string, contact: Nullable<IContact>) => {
    setFlagsToReassign((prevFlags) =>
      prevFlags.map((f) => (f.flag.code === flagCode ? { ...f, contact } : f))
    )
  }, [])

  const value = useMemo(
    (): ReassignmentContextType => ({
      availableContacts,
      rolesToReassign,
      flagsToReassign,
      updateRoleAssignment,
      updateFlagAssignment,
      setAvailableContacts,
      setRolesToReassign,
      setFlagsToReassign,
    }),
    [
      availableContacts,
      rolesToReassign,
      flagsToReassign,
      updateRoleAssignment,
      updateFlagAssignment,
    ]
  )

  return <ReassignmentContext.Provider value={value}>{children}</ReassignmentContext.Provider>
}
